<template>
  <v-container class="photos">
    <v-row justify="center">
      <ViewHeader :title="$t('message.photos.addPhoto')" />
      <v-col cols="12">
        <v-card>
          <v-row no-gutters justify="center">
            <v-col class="photos__wrapper">
              <div class="add-photo">
                <v-row
                  class="mx-0"
                  v-if="loaded && allFacilitiesIds.length > 0"
                >
                  <v-col cols="12">
                    <h3>{{ $t("message.photos.facilityLabel") }}:</h3>
                    <div
                      class="d-flex align-items-center flex-wrap border-bottom"
                    >
                      <div class="add-photo__radios">
                        <v-radio-group
                          v-model="facilitiesOptions.value"
                          class="add-photo__radio-group"
                        >
                          <div
                            v-for="(option, index) in facilitiesOptions.options"
                            :key="`option_${index}`"
                            class="d-flex align-center mb-2"
                          >
                            <v-radio
                              :name="facilitiesOptions.title"
                              :label="$t(option.title)"
                              :value="index"
                              class="mb-0 mr-2"
                            ></v-radio>
                            <v-tooltip right :max-width="436">
                              <template v-slot:activator="{ on }">
                                <div class="info-icon" v-on="on">
                                  <v-icon color="accent"
                                    >mdi-information</v-icon
                                  >
                                </div>
                              </template>
                              {{ $t(option.tooltip) }}
                            </v-tooltip>
                            <div
                              class="selected-facilities"
                              :class="{
                                'red--text': selectedFacilitiesIds.length === 0
                              }"
                              v-show="
                                index === 1 &&
                                  (selectedFacilitiesIds.length ||
                                    facilitiesOptions.value === 1)
                              "
                            >
                              <div class="mr-2">
                                {{ getSelectedFacilitiesLabel() }}
                              </div>
                              <v-btn
                                text
                                color="primary"
                                @click="filterDialog = !filterDialog"
                                >{{ $t("message.posts.changeButton") }}</v-btn
                              >
                            </div>
                          </div>

                          <FilterDialog
                            v-if="filterDialog"
                            :show-open-status="true"
                            :verified-facilities-only="true"
                            :facilities-ids="selectedFacilitiesIds"
                            :cities="cities"
                            :facilities-groups="facilitiesGroupsIds"
                            :chosen-search-type="searchType"
                            :chosen-open-status="status.open"
                            @setFacilitiesIds="selectedFacilitiesIds = $event"
                            @setCities="cities = $event"
                            @setFacilitiesGroupsIds="
                              facilitiesGroupsIds = $event
                            "
                            @setSearchType="searchType = $event"
                            @setOpenStatus="status.open = $event"
                            @change="onFiltersChange({ ...$event })"
                            :handleClose="closeFilterDialog"
                          />
                        </v-radio-group>
                      </div>
                      <div
                        class="add-photo__visibility-wrapper"
                        v-if="hasStoreLocator"
                      >
                        <div class="add-photo__visibility">
                          <div class="add-photo__visibility--label">
                            {{ $t("message.photos.list.visible") }}:
                          </div>
                          <div class="add-photo__visibility--switches">
                            <div class="d-flex align-items-center">
                              <ui-switch
                                v-model="visibleInStoreLocator"
                                :label="$t('message.posts.visibleStoreLocator')"
                                color="cyan"
                              />
                              <v-tooltip bottom :max-width="363">
                                <template v-slot:activator="{ on }">
                                  <div
                                    v-on="on"
                                    class="d-flex align-items-center ml-4"
                                  >
                                    <v-icon color="accent" size="20">
                                      mdi-information
                                    </v-icon>
                                  </div>
                                </template>
                                {{
                                  $t(
                                    `message.photos.storeLocator.${
                                      visibleInStoreLocator
                                        ? "tooltipSwitchOn"
                                        : "tooltipSwitchOff"
                                    }`
                                  )
                                }}
                              </v-tooltip>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" class="white">
                    <v-form>
                      <v-row>
                        <v-col cols="12">
                          <TransitionGroup
                            name="image-list"
                            tag="div"
                            class="add-photo__form-container"
                            @before-enter="beforeEnter"
                            @enter="enter"
                            @leave="leave"
                          >
                            <PhotoAddItem
                              v-for="item in images"
                              :key="item.id"
                              :image="item.src"
                              :file="item.file"
                              :loading-image="item.isLoading"
                              :media-types="mediaTypes"
                              :show-delete="images.length > 1"
                              @upload-image="
                                image => getImageUrl(image, item.id)
                              "
                              @media-type-change="
                                mediaType =>
                                  handleMediaTypeChange(item.id, mediaType)
                              "
                              @delete-image="handleDeleteImage(item.id)"
                              @upload-many-images="addImages"
                            />
                          </TransitionGroup>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="4" class="ml-auto">
                          <Footer class="add-photo__footer">
                            <ui-button
                              color="primary"
                              outlined
                              @click="() => addImage()"
                            >
                              {{ $t("message.photos.addMore") }}
                            </ui-button>
                            <ui-button
                              color="primary"
                              @click="sendImages"
                              :disabled="sendBtnDisabled"
                            >
                              {{ $t("message.photos.send") }}
                            </ui-button>
                          </Footer>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-col>
                </v-row>
                <v-row v-else-if="loaded && allFacilitiesIds.length === 0">
                  <v-col
                    style="min-height: 650px;"
                    cols="12"
                    class="d-flex align-center px-12"
                  >
                    <div class="no-data d-flex flex-column align-center">
                      <h2 class="subtitle mb-6">
                        {{ $t("message.photos.noData.title") }}
                      </h2>
                      <p class="no-data__description text-center">
                        {{ $t("message.photos.noData.desc") }}
                      </p>
                      <v-btn color="secondary" @click="goToGMBAccounts">
                        {{ $t("message.photos.noData.button") }}
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-row v-else></v-row>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import FilterDialog from "@/components/Filter/dialog";
import AlertDialog from "@/components/ui/AlertDialog";
import ViewHeader from "@/components/ui/ViewHeader.vue";
import PhotoAddItem from "@/components/Photos/PhotoAddItem.vue";

import facilitiesService from "@/services/facilities";
import photosService from "@/services/photos";
import serviceHandler from "@/helpers/handlers/service";
import ErrorHandler from "@/helpers/handlers/error";
import { DialogHelper } from "@/helpers/dialog-helper";
import { pluralCount } from "@/helpers/helpers";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { GMB_MEDIA_TYPE } from "@/enums/gmb-media-type";

import cuid from "cuid";
import intersection from "lodash/intersection";
import { mapState, mapActions, mapGetters } from "vuex";

export default {
  name: "AddNewPhoto",
  components: {
    FilterDialog,
    ViewHeader,
    PhotoAddItem
  },
  data() {
    return {
      loaded: false,
      loadingImage: false,
      filterDialog: false,
      previewDialog: false,
      valid: true,
      images: [
        {
          id: cuid(),
          src: null,
          isLoading: false,
          mediaType: null,
          file: null
        }
      ],
      allFacilitiesIds: [],
      selectedFacilitiesIds: [],
      cities: [],
      facilitiesGroupsIds: [],
      searchType: null,
      status: {
        open: 0
      },
      mediaTypes: [],
      mediaTypesToValidate: [
        {
          id: GMB_MEDIA_TYPE.LOGO.ID,
          name: this.$t(`message.photos.list.${GMB_MEDIA_TYPE.LOGO.NAME}`)
        },
        {
          id: GMB_MEDIA_TYPE.COVER.ID,
          name: this.$t(`message.photos.list.${GMB_MEDIA_TYPE.COVER.NAME}`)
        }
      ],
      facilitiesOptions: {
        name: "facilities",
        id: cuid(),
        required: true,
        value: 1,
        options: [
          {
            title: "message.photos.facilitiesAll",
            tooltip: "message.photos.facilitiesAllTooltip"
          },
          {
            title: "message.photos.facilitiesSelected",
            tooltip: "message.photos.facilitiesSelectedTooltip"
          }
        ]
      },
      forms: [],
      visibleInStoreLocator: true
    };
  },

  async created() {
    const queryIds = this.getFacilitiesIdsFromQuery();
    if (queryIds.length) {
      this.selectedFacilitiesIds = queryIds;
    } else {
      this.facilitiesOptions.value = 0;
    }

    this.$store.commit("company/SET_LOADING", true);
    await this.fetchFacilitiesIds();
    await photosService.getMediaTypes().then(response => {
      this.mediaTypes = response.data;
    });
    this.loaded = true;
    this.$store.commit("company/SET_LOADING", false);
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  computed: {
    ...mapState({
      loading: state => state.company.loading
    }),
    ...mapGetters({
      hasStoreLocator: "company/hasStoreLocator"
    }),
    sendBtnDisabled() {
      return !this.images.every(
        image =>
          image.src !== null &&
          image.isLoading === false &&
          image.mediaType !== null
      );
    }
  },

  methods: {
    ...mapActions({
      resetFacilitiesStore: "facilities/resetStore",
      resetFacilitiesGroupsStore: "facilitiesGroups/resetStore"
    }),
    sanitize(obj) {
      return JSON.parse(
        JSON.stringify(obj, (key, value) => {
          return value === null ? undefined : value;
        })
      );
    },
    addMediaType(form, mediaType) {
      if (mediaType && mediaType.id) {
        form["type"] = mediaType.id;
      }
      return form;
    },
    addFacilitites(form) {
      form["facilities_ids"] = this.allFacilitiesIds.map(({ id }) => id);
      if (this.facilitiesOptions.value !== 0) {
        form["facilities_ids"] = intersection(
          this.selectedFacilitiesIds,
          form["facilities_ids"]
        );
      }
      return form;
    },
    addImageUrl(form, image) {
      if (image.src && image.src.url) {
        form["image_url"] = image.src.url;
      }
      return form;
    },
    removeEmptyEntries(formData) {
      Object.entries(formData).forEach(([key, value]) => {
        if (typeof value === "object" && !Object.keys(value).length) {
          delete formData[key];
        }
      });
      return formData;
    },
    removeEmptyEvents(formData) {
      if (formData.event) {
        Object.entries(formData.event.schedule).forEach(([key, value]) => {
          if (typeof value === "object" && !Object.keys(value).length) {
            delete formData.event.schedule[key];
          }
        });
      }
      return formData;
    },
    prepareForms() {
      this.forms = this.images.reduce((acc, image) => {
        let form = {};

        form = this.addMediaType(form, image.mediaType);
        form = this.addFacilitites(form);
        form = this.addImageUrl(form, image);

        let formData = this.sanitize(form);
        formData = this.removeEmptyEntries(formData);
        formData = this.removeEmptyEvents(formData);
        formData.add_to_store_locator = this.visibleInStoreLocator;

        acc.push({ formData, image });
        return acc;
      }, []);
    },
    validateImageType(type) {
      const filteredImages = this.images.filter(
        image => image.mediaType && image.mediaType.id === type.id
      );
      if (filteredImages.length > 1) {
        return this.$t("message.photos.countLimitError", [type.name]);
      }
    },
    validateMediaTypes() {
      return this.mediaTypesToValidate.reduce((errors, mediaType) => {
        const errorMessage = this.validateImageType(mediaType);
        if (errorMessage) {
          errors.push(errorMessage);
        }
        return errors;
      }, []);
    },
    async sendImages() {
      this.prepareForms();
      const mediaTypesErrors = this.validateMediaTypes();
      if (mediaTypesErrors.length) {
        mediaTypesErrors.forEach(error => {
          this.$notify({
            group: "default",
            type: "error",
            text: error
          });
        });
        return;
      }
      this.$store.commit("company/SET_LOADING", true);
      Promise.allSettled(
        this.forms.map(({ formData, image }) => this.send(formData, image))
      ).then(results => {
        const sentImages = results
          .filter(({ status }) => status === "fulfilled")
          .map(({ value }) => value);
        this.sendEvent(sentImages);
        if (sentImages.length === this.images.length) {
          DialogHelper.showDialog(
            AlertDialog,
            {
              text: this.$t("message.photos.photoAddedSuccessfully"),
              buttonText: this.$t("message.common.ok")
            },
            []
          );
          this.images = [];
          this.$router.push({ name: "photos" });
        } else {
          sentImages.forEach(({ id }) => {
            this.images = this.images.filter(image => image.id !== id);
          });
          DialogHelper.showDialog(
            AlertDialog,
            {
              text: this.$t("message.photos.photosNotAdded"),
              buttonText: this.$t("message.common.ok")
            },
            []
          );
        }

        this.$store.commit("company/SET_LOADING", false);
      });
    },
    send(formData, image) {
      return photosService
        .postPhoto(formData)
        .then(() => {
          return image;
        })
        .catch(error => {
          if (error.response.data.facilities_ids) {
            this.$notify({
              group: "default",
              type: "error",
              text: this.$t("message.photos.facilitiesError")
            });
          } else {
            ErrorHandler.displayError(error);
          }
          throw error;
        });
    },
    sendEvent(sentImages) {
      const photos = sentImages.map(image =>
        image.src
          ? {
              height_px: image.src.height || null,
              width_px: image.src.width || null,
              size_kb: Math.round((image.src.size / 1024) * 100) / 100 || null,
              edited: image.src.edited
            }
          : null
      );

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.PHOTO_MASS_ADD,
        locations_selection_type:
          this.facilitiesOptions.value === 0 ? "all" : "selected",
        num_locations:
          this.facilitiesOptions.value === 0
            ? this.allFacilitiesIds.length
            : this.selectedFacilitiesIds.length,
        num_photos: sentImages.length,
        categories: sentImages.map(image =>
          image.mediaType ? image.mediaType.id : null
        ),
        photos: JSON.stringify(photos)
      });
    },

    async getImageUrl({ file, edited }, imageId) {
      if (file) {
        const selectedImage = this.images.find(({ id }) => id === imageId);
        selectedImage.isLoading = true;
        let formData = new FormData();
        formData.append("image", file);

        await photosService
          .getImageUrl(formData)
          .then(response => {
            selectedImage.src = {
              url: response.data.image,
              ...response.data.meta,
              edited
            };
          })
          .catch(error => {
            ErrorHandler.displayError(error);
            selectedImage.src = null;
          })
          .finally(() => {
            selectedImage.isLoading = false;
          });
      }
    },
    addImages(images) {
      images.forEach(image => {
        const imageId = this.addImage(image);
        this.getImageUrl({ file: image, edited: false }, imageId);
      });
    },

    showPreview() {
      this.previewDialog = true;
    },

    goToGMBAccounts() {
      this.$router.push({ name: "profile-gmb-accounts" });
    },

    async fetchFacilitiesIds() {
      const results = await serviceHandler(
        facilitiesService.getFacilitiesIds({
          is_owner: true,
          is_verified: true,
          facilities_ids: this.selectedFacilitiesIds,
          cities: this.cities,
          groups: this.facilitiesGroupsIds,
          open_status: this.status.open
        })
      );

      if (
        this.searchType === "facilities" ||
        this.searchType === "city" ||
        this.searchType === "facilitiesGroups"
      ) {
        this.selectedFacilitiesIds = results.map(({ id }) => id);
      } else {
        if (results.length > 0) {
          this.allFacilitiesIds = results;
        }
      }
    },

    getSelectedFacilitiesLabel() {
      let messagePath = "message.posts.selectedFacilities";

      if (this.searchType === "city") {
        messagePath = "message.posts.selectedCities";
      }

      if (this.searchType === "facilitiesGroups") {
        messagePath = "message.posts.selectedFacilitiesGroups";
      }

      return this.$t(messagePath, {
        facilitiesCount: this.getCountLabel(
          "facility",
          this.selectedFacilitiesIds.length
        ),
        citiesCount: this.getCountLabel("city", this.cities.length),
        facilitiesGroupsCount: this.getCountLabel(
          "groupGenitive",
          this.facilitiesGroupsIds.length
        )
      });
    },

    getCountLabel(type, value) {
      return pluralCount(`message.pluralForms.${type}`, value);
    },

    getFacilitiesIdsFromQuery() {
      return this.$route.query.ids
        ? this.$route.query.ids.split(",").map(id => +id)
        : [];
    },

    async onFiltersChange({
      facilitiesIds = this.selectedFacilitiesIds,
      cities = this.cities,
      facilitiesGroupsIds = this.facilitiesGroupsIds,
      openStatus = this.status.open
    }) {
      this.$store.commit("company/SET_LOADING", true);

      this.selectedFacilitiesIds = facilitiesIds;
      this.cities = cities;
      this.facilitiesGroupsIds = facilitiesGroupsIds;
      this.status.open = openStatus;

      await this.fetchFacilitiesIds();

      this.$store.commit("company/SET_LOADING", false);
    },

    closeFilterDialog() {
      this.filterDialog = false;
    },

    changePhoto() {
      this.$refs["photo-add"].$refs.input.click();
    },

    handleMediaTypeChange(imageId, mediaType) {
      const imageIndex = this.images.findIndex(({ id }) => id === imageId);
      this.images[imageIndex].mediaType = mediaType;
    },
    addImage(file) {
      const imageId = cuid();
      this.images.push({
        id: imageId,
        src: null,
        isLoading: false,
        mediaType: null,
        file: file || null
      });
      return imageId;
    },
    handleDeleteImage(imageId) {
      this.images = this.images.filter(({ id }) => id !== imageId);
    },
    beforeEnter(el) {
      el.style.opacity = 0;
      el.style.transform = "translateY(-30px)";
    },
    enter(el, done) {
      setTimeout(() => {
        el.style.transition = "all 0.5s ease";
        el.style.opacity = 1;
        el.style.transform = "translateY(0)";
        done();
      }, 0);
    },
    leave(el, done) {
      el.style.transition = "all 0.5s ease";
      el.style.opacity = 0;
      el.style.transform = "translateY(-30px)";
      setTimeout(() => {
        done();
      }, 500);
    }
  },

  watch: {
    "facilitiesOptions.value"(newValue) {
      if (newValue === 1) {
        this.filterDialog = true;
      } else {
        this.filterDialog = false;
        this.selectedFacilitiesIds = [];
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.photos {
  &__wrapper {
    padding: $spacing * 2;
  }
}
</style>

<style lang="scss">
.add-photo {
  min-height: 650px;

  &__radio-group {
    padding-bottom: 0;
  }

  &__footer {
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin-bottom: $spacing * 4;
    gap: $spacing * 2;

    > button {
      flex: 1;
    }
  }

  &__radios {
    flex: 1 0 auto;
  }

  &__visibility-wrapper {
    max-width: 100%;
    min-width: 350px;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
  }

  &__visibility {
    background: $light-4;
    border-radius: 8px;
    padding: $spacing * 1.5 $spacing * 3;
    flex: 1;
    margin: $spacing * 2 0;

    &--label {
      @include font-size(12px);
      line-height: 17px;
      color: rgba($dark-blue-3, 0.9);
      font-weight: normal;
    }
  }

  &__form-container {
    display: flex;
    flex-direction: column;
    gap: $spacing * 4;
  }
}

.selected-facilities {
  display: flex;
  align-items: center;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.02em;
  color: rgba($gray-darker-2, 0.5);
  margin-left: $spacing * 2;
  height: 24px;
}
</style>
